import { NodeItem, NodeType } from '@/views/flow/nodeview/NodeItem';
import { createSingleton } from './SingletonFactory';
import AIServices from './ai/AIServices';

export enum Model3DType {
  HYPER3D_RODIN = 'Rodin',
  HUNYUAN3D_TURBO = 'Hunyuan3D',
  MESHY_V5 = 'MeshyV5',
  MESHY_V6 = 'MeshyV6',
}

class T3dEngineer {
  private aiServices = new AIServices();

  //rodin 만 지원
  async generateFromPrompt(
    options?: {
      // Hunyuan3D Turbo options
      seed?: number;
      num_inference_steps?: number;
      guidance_scale?: number;
      octree_resolution?: number;
      textured_mesh?: boolean;
      // Hyper3D Rodin options
      prompt?: string;
      condition_mode?: 'fuse' | 'concat';
      geometry_file_format?: 'glb' | 'usdz' | 'fbx' | 'obj' | 'stl';
      material?: 'PBR' | 'Shaded';
      quality?: 'high' | 'medium' | 'low' | 'extra-low';
      use_hyper?: boolean;
      tier?: 'Regular' | 'Sketch';
      TAPose?: boolean;
      addons?: string;
    },
    signal?: AbortSignal,
  ): Promise<{
    model: { url: string; file_size: number; file_name: string; content_type: string };
    timings?: any;
  }> {
    // const srcNodes = node.source.map((id) => allNode.find((n) => n.id === id)!);
    try {
      const response = await this.aiServices.getImageTo3DAI().generateWithHyper3DRodin(undefined, {
        prompt: options?.prompt || 'Create a detailed 3D model of this object',
        condition_mode: options?.condition_mode || 'fuse',
        geometry_file_format: options?.geometry_file_format || 'glb',
        material: options?.material || 'PBR',
        quality: options?.quality || 'medium',
        use_hyper: options?.use_hyper || false,
        tier: options?.tier || 'Regular',
        TAPose: options?.TAPose || false,
        addons: options?.addons,
        seed: options?.seed,
        signal,
      });

      // console.log(response);
      return response;
    } catch (error) {
      return Promise.reject(error instanceof Error ? error.message : '3D model generation failed');
    }
  }
  async generate(
    node: NodeItem,
    allNode: NodeItem[],
    options?: {
      // Hunyuan3D Turbo options
      seed?: number;
      num_inference_steps?: number;
      guidance_scale?: number;
      octree_resolution?: number;
      textured_mesh?: boolean;
      // Hyper3D Rodin options
      prompt?: string;
      condition_mode?: 'fuse' | 'concat';
      geometry_file_format?: 'glb' | 'usdz' | 'fbx' | 'obj' | 'stl';
      material?: 'PBR' | 'Shaded';
      quality?: 'high' | 'medium' | 'low' | 'extra-low';
      use_hyper?: boolean;
      tier?: 'Regular' | 'Sketch';
      TAPose?: boolean;
      addons?: string;
      //meshy
      symmetry_mode?: 'auto' | 'on' | 'off';
      texture_prompt?: string;
      texture_image_url?: string;
      topology?: 'quad' | 'triangle';
    },
    signal?: AbortSignal,
  ): Promise<{
    model: { url: string; file_size: number; file_name: string; content_type: string };
    timings?: any;
  }> {
    const srcNodes = node.source.map((id) => allNode.find((n) => n.id === id)!);
    const imgNode = srcNodes.find((n) => n.type === NodeType.IMAGE);
    const promptNode = srcNodes.find((n) => n.type === NodeType.PROMPT);
    // if (!imgNode) {
    //   return Promise.reject('no image source');
    // }
    const modelType = node.model;

    let prompt = options?.prompt || 'Create a detailed 3D model of this object';
    if (promptNode?.text) prompt = promptNode.text;

    try {
      let response;
      if (imgNode && imgNode.content) {
        if (modelType === Model3DType.HYPER3D_RODIN) {
          // Use Hyper3D Rodin for high quality
          response = await this.aiServices
            .getImageTo3DAI()
            .generateWithHyper3DRodin(imgNode.content, {
              prompt: prompt,
              condition_mode: options?.condition_mode || 'fuse',
              geometry_file_format: options?.geometry_file_format || 'glb',
              material: options?.material || 'PBR',
              quality: options?.quality || 'medium',
              use_hyper: options?.use_hyper || false,
              tier: options?.tier || 'Regular',
              TAPose: options?.TAPose || false,
              addons: options?.addons,
              seed: options?.seed,
              signal,
            });
        } else if (modelType === Model3DType.HUNYUAN3D_TURBO) {
          // Use Hunyuan3D Turbo for speed (default)
          response = await this.aiServices
            .getImageTo3DAI()
            .generateWithHunyuan3DTurbo(imgNode.content, {
              seed: options?.seed || Math.floor(Math.random() * 2147483647),
              guidance_scale: options?.guidance_scale || 7.5,
              num_inference_steps: options?.num_inference_steps || 50,
              octree_resolution: options?.octree_resolution || 256,
              textured_mesh: options?.textured_mesh || false,
              signal,
            });
        } else if (modelType === Model3DType.MESHY_V5) {
          response = await this.aiServices.getImageTo3DAI().generateWithMeshyV5([imgNode.content], {
            topology: options?.topology,
            symmetry_mode: options?.symmetry_mode,
            texture_prompt: options?.texture_prompt,
            texture_image_url: options?.texture_image_url,
            is_a_t_pose: options?.TAPose,
            signal,
          });
        } else if (modelType === Model3DType.MESHY_V6) {
          response = await this.aiServices.getImageTo3DAI().generateWithMeshyV6(imgNode.content, {
            topology: options?.topology,
            symmetry_mode: options?.symmetry_mode,
            texture_prompt: options?.texture_prompt,
            texture_image_url: options?.texture_image_url,
            is_a_t_pose: options?.TAPose,
            signal,
          });
        }
      } else {
        if (modelType === Model3DType.HUNYUAN3D_TURBO) {
          return Promise.reject('Incompatible AI model');
        } else {
          if (!promptNode?.text) return Promise.reject('no prompt source');
          else response = await this.generateFromPrompt({ ...options, prompt: prompt });
        }
      }

      console.log(response);
      return response as any;
    } catch (error) {
      return Promise.reject(error instanceof Error ? error.message : '3D model generation failed');
    }
  }

  genereteInEdit(
    model: string,
    imgUrl: string,
    options?: {
      // Hunyuan3D Turbo options
      seed?: number;
      num_inference_steps?: number;
      guidance_scale?: number;
      octree_resolution?: number;
      textured_mesh?: boolean;
      // Hyper3D Rodin options
      prompt?: string;
      condition_mode?: 'fuse' | 'concat';
      geometry_file_format?: 'glb' | 'usdz' | 'fbx' | 'obj' | 'stl';
      material?: 'PBR' | 'Shaded';
      quality?: 'high' | 'medium' | 'low' | 'extra-low';
      use_hyper?: boolean;
      tier?: 'Regular' | 'Sketch';
      TAPose?: boolean;
      addons?: string;
      //meshy
      symmetry_mode?: 'auto' | 'on' | 'off';
      texture_prompt?: string;
      texture_image_url?: string;
      topology?: 'quad' | 'triangle';
    },
    signal?: AbortSignal,
  ) {
    if (imgUrl) {
      if (model === Model3DType.HYPER3D_RODIN) {
        return this.aiServices.getImageTo3DAI().generateWithHyper3DRodin(imgUrl, {
          prompt: options?.prompt || 'Create a detailed 3D model of this object',
          condition_mode: options?.condition_mode || 'fuse',
          geometry_file_format: options?.geometry_file_format || 'glb',
          material: options?.material || 'PBR',
          quality: options?.quality || 'medium',
          use_hyper: options?.use_hyper || false,
          tier: options?.tier || 'Regular',
          TAPose: options?.TAPose || false,
          addons: options?.addons,
          seed: options?.seed,
          signal: signal,
        });
      } else if (model === Model3DType.MESHY_V5) {
        return this.aiServices.getImageTo3DAI().generateWithMeshyV5([imgUrl], {
          topology: options?.topology,
          symmetry_mode: options?.symmetry_mode,
          texture_prompt: options?.texture_prompt,
          texture_image_url: options?.texture_image_url,
          is_a_t_pose: options?.TAPose,
          signal,
        });
      } else if (model === Model3DType.MESHY_V6) {
        return this.aiServices.getImageTo3DAI().generateWithMeshyV6(imgUrl, {
          topology: options?.topology,
          symmetry_mode: options?.symmetry_mode,
          texture_prompt: options?.texture_prompt,
          texture_image_url: options?.texture_image_url,
          is_a_t_pose: options?.TAPose,
          signal,
        });
      } else {
        return this.aiServices.getImageTo3DAI().generateWithHunyuan3DTurbo(imgUrl, {
          seed: options?.seed || Math.floor(Math.random() * 2147483647),
          guidance_scale: options?.guidance_scale || 7.5, //options?.guidance_scale || 7.5,
          num_inference_steps: 50, // options?.num_inference_steps || 50,
          octree_resolution: 256, // options?.octree_resolution || 256,
          textured_mesh: options?.textured_mesh || false, // options?.textured_mesh || false,
          signal,
        });
      }
    } else {
      if (model === Model3DType.HUNYUAN3D_TURBO) return Promise.reject('Incompatible AI model');
      else return this.generateFromPrompt(options);
    }
  }
}

export default createSingleton(T3dEngineer);
